/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Dropdown, Menu } from 'antd';
import { IconName, IconPrefix } from '@fortawesome/fontawesome-svg-core';
import { useIntl } from 'react-intl';
import { DropDownProps } from 'antd/lib/dropdown';

import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import dataContextMenu from '~src/data/dataContextMenu';
import { DB_STATE, RIGHTS_LEVEL } from '~src/types/enums';
import IconButton from '~components/_common/IconButton/IconButton';
import {
  useIsAdministrationMode,
  useIsSuperuser,
} from '~src/hooks/useIsSuperuser';
import { DbFile, DbFolder, DbFocusOptions, DbModuleFile } from '~src/types/db';
import { useFolders } from '~src/hooks/api-hooks/useFolders';
import { isFolderAvailable } from '../FileTables/FileTable.utils';
import { useIsPath } from '~src/hooks/useIsPath';
import { useCurrentFolder } from '~src/hooks/useCurrentFolder';
import { useContext, useRef } from 'react';
import _ from 'lodash';
import theme from '~src/styles/themes/js/light-theme';
import ModuleTenderHammer from '~src/assets/nav-icons/module-tender-hammer.svg';
import { NavIcon } from '~components/_common/NavIcon/NavIcon';
import { useArchiveSettings } from '~src/hooks/api-hooks/useArchiveSettings';
// import { styleFn } from './styles';

import FileVersionsContext, {
  FileVersionSelection,
} from '~src/contexts/FileVersionsContext';
import AutodeskIcon from '~src/assets/svf-icons/autodesk.svg';
import { SVFIconType } from '~components/atoms/Viewers/SVFViewer/components/SVFIconType';
import SVFIconButton from '~components/_common/SVFViewerIconButton/SVFIconButton';
import { ReactComponent as AutodeskRightClickIcon } from '~src/assets/svf-icons/autodeskRightClick.svg';

export interface RightClickOverlayProps extends Partial<DropDownProps> {
  /** menu items */
  items: typeof dataContextMenu.file;
  /** parent permission level to check against for disabling items */
  permissionLevel: RIGHTS_LEVEL | 0;
  /** item click handler */
  onItemClick: (
    item: typeof dataContextMenu.folder[0],
    option?: number
  ) => void;
  /** emply right click when overlay dissappears */
  onOverlayClose: () => void;
  /** selected item in the table */
  selected?: { folder?: DbFolder; file?: DbFile; moduleFile?: DbModuleFile };
  /** is item from left or right side */
  leftSide?: boolean;
  focusOptions?: DbFocusOptions[];
}
/** Resource rightclick (context) menu; */
export const RightClickOverlay: React.FC<RightClickOverlayProps> = ({
  items,
  permissionLevel,
  onItemClick,
  children,
  onOverlayClose,
  selected,
  leftSide,
  focusOptions,
  ...dropdownProps
}) => {
  const styles = useStyles(styleFn);
  const intl = useIntl();
  const isSuperuser = useIsSuperuser();
  const isUserInAdministrationMode = useIsAdministrationMode();
  const archiveSettings = useArchiveSettings();
  const isTenderingOn =
    archiveSettings.data?.resArchiveSettings?.is_tendering_on;
  const foldersQuery = useFolders(
    {
      as_admin:
        window.sessionStorage.getItem('appState') === 'superUser'
          ? true
          : undefined,
    },
    { enabled: false }
  );
  const currentFolder = useCurrentFolder();
  const isOnRoot = useIsPath('/archive', { endsWith: true });

  const { fileVersionsContext } = useContext(FileVersionsContext);

  // if selected folder got ANY deeper lvl Tender folder, that converting selected folder is removed
  const hasTenderChildFolder = (
    folders: DbFolder[] | undefined,
    parentFolderId?: number | null
  ): boolean => {
    if (!folders || parentFolderId == null) return false;

    const queue: number[] = [parentFolderId];

    while (queue.length) {
      const currentId = queue.shift() as number;

      const children = folders.filter(
        (folder) => folder.parent_id === currentId
      );

      for (const child of children) {
        const option = child.option as any;
        const isTenderFolder = option?.is_tender_folder === true;
        const isRootTenderFolder = option?.is_tender_root_folder === true;

        if (isTenderFolder || isRootTenderFolder) {
          return true;
        }

        queue.push(child.id);
      }
    }

    return false;
  };

  const menu = (
    <Menu css={styles.menu}>
      {items.map((item) => {
        const selectedFolderOption = selected?.folder?.option;

        // Hide / filter "add-to-tender" item
        if (item.name === 'add-to-tender') {
          // 1) If tendering is off in archive settings → never show
          if (!isTenderingOn) {
            return null;
          }

          // 2) If we're in Super User UI mode → hide this action
          // JIRA: TV1-48
          if (isUserInAdministrationMode) {
            return null;
          }

          // 3) If folder is already Tender root/folder → no sense to show button
          if (
            selectedFolderOption?.is_tender_root_folder === true ||
            selectedFolderOption?.is_tender_folder === true
          ) {
            return null;
          }

          // 4) NEW RULE:
          //    Do not allow converting a folder if ANY of its descendants
          //    is already a Tender folder (or Tender root folder)
          if (
            selected?.folder &&
            hasTenderChildFolder(
              foldersQuery.data as DbFolder[],
              selected.folder.id
            )
          ) {
            // hide the "add-to-tender" action entirely
            return null;
          }
        }

        // hide su options for normal users
        if (item.needsSU && !isSuperuser) {
          return null;
        }

        if (
          window.sessionStorage.getItem('appState') === 'timeMachine' &&
          !item.onTimeMachine
        ) {
          return null;
        }

        let disabled;

        // special case of 'rights-managment' and 'restore-to', should be enabled for SU
        if (
          (item.name === 'rights-managment' || item.name === 'restore-to') &&
          isSuperuser
        ) {
          disabled = false;
        } else {
          disabled = permissionLevel < item.minPermissionLevel;
        }

        if (
          window.sessionStorage.getItem('appState') === 'superUser' &&
          isSuperuser
        ) {
          disabled = false;
        }

        if (item.name === 'restore' && isSuperuser && selected?.file) {
          const availableFolder =
            foldersQuery.data &&
            isFolderAvailable(selected.file.folder_id, foldersQuery.data);
          if (availableFolder) {
            disabled = false;
          } else {
            disabled = true;
          }
        }

        if (!selected?.folder && !selected?.file && !selected?.moduleFile) {
          permissionLevel = currentFolder?.permission || 0;
        }

        if (item.name === 'create-folder') {
          if (!isOnRoot) {
            disabled = permissionLevel < RIGHTS_LEVEL.WRITE;
          } else if (isSuperuser) {
            disabled = false;
          }
        }

        if (selected?.moduleFile) {
          disabled = false;
        }

        if (
          item.name === 'delete' &&
          isSuperuser &&
          selected?.folder &&
          selected?.folder?.state === DB_STATE.REMOVED
        ) {
          disabled = true;
        }

        if (item.disabled) {
          disabled = true;
          return null;
        }

        disabled = false;

        if (item.name === 'svf-view-file' && selected?.file) {
          const version = fileVersionsContext[selected.file.id];
          if (version == null) {
            return null;
          }
          const svfState = version.extension_svf_model?.state || 0;
          const hasModel = version.extension_svf_model != null;
          const isConvertible = version.is_svf_convertible === true;
          const shouldShow = isConvertible || hasModel;

          if (!shouldShow) {
            return null;
          }

          return (
            <Menu.SubMenu
              disabled={disabled}
              key={item.key}
              css={styles.submenu}
              title={
                <div css={styles.titleRow}>
                  <AutodeskRightClickIcon css={styles.customSvgIcon} />
                  <span css={styles.text}>
                    {intl.formatMessage({ id: item.name })}
                  </span>
                </div>
              }>
              {!hasModel && isConvertible && (
                <Menu.Item
                  key={'subMenuSVFConvert'}
                  css={styles.itemFocus}
                  onClick={() => onItemClick(item, 0)}>
                  <span css={[styles.text]}>
                    {intl.formatMessage({ id: 'svf-view-file-convert' })}
                  </span>
                </Menu.Item>
              )}

              {[1, 2, 4].includes(svfState | 0) && (
                <Menu.Item
                  key={'subMenuSVFFullProgress'}
                  css={styles.itemFocus}
                  onClick={() => onItemClick(item, 1)}>
                  <span css={[styles.text]}>
                    {intl.formatMessage({ id: 'svf-view-file-progress' })}
                  </span>
                </Menu.Item>
              )}

              {svfState === 7 && (
                <Menu.Item
                  key={'subMenuSVFWindow'}
                  css={styles.itemFocus}
                  onClick={() => onItemClick(item, 2)}>
                  <span css={[styles.text]}>
                    {intl.formatMessage({ id: 'svf-view-file-window' })}
                  </span>
                </Menu.Item>
              )}

              {svfState === 7 && (
                <Menu.Item
                  key={'subMenuSVFFullscreen'}
                  css={styles.itemFocus}
                  onClick={() => onItemClick(item, 3)}>
                  <span css={[styles.text]}>
                    {intl.formatMessage({ id: 'svf-view-file-fullscreen' })}
                  </span>
                </Menu.Item>
              )}

              {svfState === 5 && (
                <Menu.Item
                  key={'subMenuSVFRetry'}
                  css={styles.itemFocus}
                  onClick={() => onItemClick(item, 0)}>
                  <span css={[styles.text]}>
                    {intl.formatMessage({ id: 'svf-view-file-retry' })}
                  </span>
                </Menu.Item>
              )}

              {svfState === 7 && isSuperuser && (
                <Menu.Item
                  key={'subMenuSVFSetPreview'}
                  css={styles.itemFocus}
                  onClick={() => onItemClick(item, 4)}>
                  <span css={[styles.text]}>
                    {intl.formatMessage({ id: 'set-primary-preview' })}
                  </span>
                </Menu.Item>
              )}
            </Menu.SubMenu>
          );
        }

        return item.isSubMenu && focusOptions ? (
          <Menu.SubMenu
            disabled={disabled}
            icon={
              <IconButton
                icon={[item.iconStyle as IconPrefix, item.icon as IconName]}
                customStyle={styles.icon}
                backgroundOff
              />
            }
            key={item.key}
            css={styles.item}
            title={
              <p css={styles.text}>{intl.formatMessage({ id: item.name })}</p>
            }>
            <Menu.Item
              key={'subMenuNoHighLight'}
              css={styles.itemFocus}
              onClick={() => onItemClick(item, -1)}>
              <span
                css={[
                  styles.text,
                  {
                    marginBottom: '5px',
                    borderBottom: `1px solid ${theme.colors.lightGray}`,
                  },
                ]}>
                {intl.formatMessage({ id: 'no-highlight' })}
              </span>
            </Menu.Item>
            {focusOptions?.map((option) => {
              return (
                <Menu.Item
                  key={option.value + 'subMenu'}
                  css={styles.itemFocus}
                  onClick={() => onItemClick(item, option.value)}>
                  <div
                    css={[
                      styles.colorItemContainer,
                      {
                        border:
                          selected?.folder?.focus === option.value
                            ? `1.5px solid ${theme.colors.primary}`
                            : '',
                      },
                    ]}>
                    <div
                      css={[
                        styles.colorItem,
                        { background: `#${option.color_hex_code}` },
                      ]}
                    />
                  </div>
                </Menu.Item>
              );
            })}
            <Menu.Item
              key={'subMenuNoHighLightIcon'}
              css={styles.itemFocus}
              onClick={() => onItemClick(item, -1)}>
              <span
                css={[
                  styles.text,
                  {
                    width: '100%',
                    marginTop: '5px',
                    borderTop: `1px solid ${theme.colors.lightGray}`,
                  },
                ]}>
                <IconButton
                  iconStyle="far"
                  icon="info-circle"
                  customStyle={styles.icons}
                  backgroundOff
                  tooltip={
                    intl.locale === 'da' ? (
                      <div>
                        <h3 css={styles.whiteHeader}>Mappe Highlight farver</h3>
                        <p>
                          Her kan der vælges om enkelte mapper skal have mere
                          fremtrædende synlighed ved at give dem en bestemt
                          baggrundsfarve.
                        </p>
                        <p>
                          <b>Rettigheder</b>
                          <br />
                          Alle brugere med redigeringsrettigheder til en mappe
                          kan vælge mappe highlight farver.
                        </p>
                        <p>
                          <b>Note</b>
                          <br />
                          Vær opmærksom på at de valgte farver vil være synlige
                          for alle brugere på projektet. Mappe Highlight er en
                          funktion der giver de projektstyrende mulighed for at
                          give enkelte mapper ekstra fremtrædende synlighed for
                          både dem selv og for alle andre på projektet.
                          <br />
                          Hvis det ønskes at enkelte mapper bliver fremtrædende
                          for en selv, kan Favorit funktionaliteten benyttes.
                        </p>
                      </div>
                    ) : (
                      <div>
                        <h3 css={styles.whiteHeader}>Folder Highlights</h3>
                        <p>
                          A folder can be highlighted in order to make it
                          visually more accessible for everyone. This
                          functionality will add a background color to the
                          selected folder.
                        </p>
                        <p>
                          <b>Permissions</b>
                          <br />
                          All users with the permission to edit the folder have
                          permissions to edit the selected highlight color.
                        </p>
                        <p>
                          <b>Note</b>
                          <br />
                          Be aware that selected highlight colors are visible
                          for all users on the project. The Folder Highlight
                          functionality will give Project Managers the
                          opportunity to make specific folders more visible for
                          everyone.
                          <br />
                          To get easier access to specific folders on your own
                          account, use the Favorites functionality.
                        </p>
                      </div>
                    )
                  }
                  widthTooltip="500px"
                />
              </span>
            </Menu.Item>
          </Menu.SubMenu>
        ) : (
          <Menu.Item
            key={item.key}
            css={styles.item}
            disabled={disabled}
            title={
              disabled
                ? intl.formatMessage({ id: 'permission-restricted' })
                : undefined
            }
            onClick={() => onItemClick(item)}>
            {item.name === 'add-to-tender' ? (
              <NavIcon
                customStyle={{
                  width: '26px',
                  height: '17px',
                  marginRight: '7px',
                }}
                icon={ModuleTenderHammer}
              />
            ) : (
              <IconButton
                icon={[item.iconStyle as IconPrefix, item.icon as IconName]}
                customStyle={styles.icon}
                backgroundOff
              />
            )}
            <span css={styles.text}>
              {intl.formatMessage({ id: item.name })}
            </span>
          </Menu.Item>
        );
      })}
    </Menu>
  );

  return (
    <Dropdown
      onVisibleChange={(visible) => {
        if (!visible) onOverlayClose();
      }}
      overlay={menu}
      //visible={!!selected?.file || !!selected?.folder}
      trigger={['contextMenu']}
      {...dropdownProps}>
      <div
        id="mask"
        className="site-dropdown-context-menu"
        tabIndex={0} // Make the div focusable
        onTouchEnd={(e) => {
          // Handle touch events
          e.preventDefault();
          // You might need to toggle the visibility of the dropdown manually here
        }}>
        {children}
      </div>
    </Dropdown>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  menu: {
    padding: '5px 0px',
  },

  // SVF right click menu
  customSvgIcon: {
    width: '18px',
    height: '18px',
    color: theme.colors.secondary,
    marginRight: '8px',
    flexShrink: 0,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: '5px',
  },
  submenu: {
    '& .ant-dropdown-menu-submenu-title': {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      padding: '2px 15px',
      cursor: 'pointer',
      '&:active': {
        backgroundColor: theme.colors.primaryLight,
      },
      '&:hover': {
        backgroundColor:
          window.sessionStorage.getItem('appState') === 'superUser' ||
          window.sessionStorage.getItem('appState') === 'timeMachine'
            ? `${theme.colors.secondaryLight} !important`
            : '',
      },
      '&:not(.ant-dropdown-menu-item-disabled)': {
        color: theme.colors.text.primary,
      },

      'div, span': {
        background: 'transparent !important',
      },
    },
  },

  item: {
    display: 'flex',
    flexDirection: 'row',
    padding: '2px 15px',
    cursor: 'pointer',

    '&:active': {
      backgroundColor: theme.colors.primaryLight,
    },
    '&:hover': {
      backgroundColor:
        window.sessionStorage.getItem('appState') === 'superUser' ||
        window.sessionStorage.getItem('appState') === 'timeMachine'
          ? `${theme.colors.secondaryLight} !important`
          : '',
    },

    '&:not(.ant-dropdown-menu-item-disabled)': {
      color: theme.colors.text.primary,
    },
    div: {
      background: 'transparent !important',
      padding: 0,
      span: {
        background: 'transparent !important',
        '&:not(.ant-dropdown-menu-title-content)': { paddingTop: '2.5px' },
      },
    },
  },
  itemFocus: {
    span: { display: 'flex', justifyContent: 'flex-start' },
    display: 'flex',
    flexDirection: 'row',
    padding: '2px 5px',
    cursor: 'pointer',
    justifyContent: 'flex-start',

    '&:active': {
      backgroundColor: theme.colors.primaryLight,
    },
  },
  icon: {
    color: theme.colors.secondary,
    fontSize: '16px',
  },
  text: {
    display: 'inline',
    fontSize: '16px',
    padding: '3px 6px',
    userSelect: 'none',
  },
  colorItemContainer: {
    width: '68px',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid',
    borderColor: theme.colors.lightGray,
  },
  colorItem: {
    width: '62px',
    height: '22px',
    border: '1px solid',
    borderColor: theme.colors.lightGray,
  },
  icons: {
    fontSize: '20px',
    color: theme.colors.primary,
    transition: '0.3s',
  },
  whiteHeader: {
    color: 'white',
  },
});
